// Contract compliance: every obligation of the Driver Employment Agreement v6,
// Addendum No. 1 and the Code of Conduct, with its status, the deadlines the
// agreement sets and who owns them, what is overdue, and each driver's written
// records. db/migrations/3800 to 3806, docs/CONTRACT-COMPLIANCE.md.
//
// The page reads; Postgres decides. Every refusal is Postgres's sentence,
// unaltered. Signing a record means the driver received it, never that he
// agrees, and the page says so wherever a signature is asked for.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ClipboardCheck, FileText, Gauge, ListChecks, Scale, ShieldAlert, Users } from 'lucide-react'
import {
  useChangeContractSetting, useConfirmRecordWitness, useGetContractCompliance, useGetDriverRecords, useOpenRecordFile,
  useRecordPaperRecord, useRecordRecordRefusal, useSignRecordUpload, useVoidEmployeeRecord,
} from '../hooks/backend/compliance'
import { Button } from '../lib/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../lib/shadcn/dialog'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Spinner } from '../lib/shadcn/spinner'
import { Textarea } from '../lib/shadcn/textarea'
import { toast } from '../lib/shadcn/sonner'
import { writeErrorMessage } from '../utils/writeError'
import {
  MESSAGE_STATUS, OBLIGATION_STATUS, RECORD_STATUS, day, todaySast,
  type CompliancePage, type DriverRecords, type DriverRow, type EmployeeRecord, type Obligation, type Setting,
} from '../utils/compliance'

type Tab = 'obligations' | 'deadlines' | 'drivers' | 'numbers'

export default function ContractCompliance() {
  const pageQuery = useGetContractCompliance<CompliancePage | null>()
  const load = pageQuery.trigger
  const refresh = useCallback(() => { void load({}, { skipCache: true }).catch(() => undefined) }, [load])
  useEffect(() => { refresh() }, [refresh])
  const [tab, setTab] = useState<Tab>('obligations')
  const [driver, setDriver] = useState<DriverRow | null>(null)

  const page = pageQuery.data
  if (pageQuery.loading && !page && !pageQuery.error) {
    return <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading…</div>
  }
  if (pageQuery.error && !page) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md space-y-3 rounded-lg border border-border bg-card p-6 text-center text-sm">
          <p className="text-rose-700 dark:text-rose-300">{pageQuery.error}</p>
          <Button size="sm" variant="outline" onClick={refresh}>Try again</Button>
        </div>
      </div>
    )
  }
  if (!page) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <ShieldAlert className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">Contract compliance is open to staff logins.</p>
          <p className="text-xs text-muted-foreground">Ask an admin to switch on View for you in cockpit access.</p>
        </div>
      </div>
    )
  }
  const s = page.summary
  const tabs: { id: Tab; label: string; icon: typeof ListChecks; badge?: number }[] = [
    { id: 'obligations', label: 'Obligations', icon: ListChecks },
    { id: 'deadlines', label: 'Deadlines', icon: AlertTriangle, badge: s.overdue_deadlines },
    { id: 'drivers', label: 'Drivers and records', icon: Users, badge: s.records_overdue },
    { id: 'numbers', label: 'Contract numbers', icon: Scale, badge: s.setting_drift },
  ]

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
          <ClipboardCheck className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-foreground">Contract compliance</h1>
          <p className="text-xs text-muted-foreground">
            Driver Employment Agreement v6, Addendum No. 1 and the Code of Conduct. As at {day(page.as_of)}.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={refresh}>Refresh</Button>
      </div>

      <div className="grid grid-cols-2 gap-2 border-b border-border px-4 py-3 sm:grid-cols-4 lg:grid-cols-7">
        <Stat label="Obligations" value={s.obligations} />
        <Stat label="In place" value={s.in_place} tone="text-emerald-700 dark:text-emerald-300" />
        <Stat label="Partial" value={s.partial} tone="text-amber-700 dark:text-amber-300" />
        <Stat label="Missing" value={s.missing} tone="text-rose-700 dark:text-rose-300" />
        <Stat label="Open deadlines" value={s.open_deadlines} />
        <Stat label="Overdue" value={s.overdue_deadlines} tone={s.overdue_deadlines ? 'text-rose-700 dark:text-rose-300' : undefined} />
        <Stat label="Records to acknowledge" value={s.records_awaiting} sub={s.records_overdue ? `${s.records_overdue} past their date` : undefined} />
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-border px-3">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm ${tab === t.id ? 'border-[hsl(var(--brand))] font-semibold text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            <t.icon className="h-4 w-4" /> {t.label}
            {t.badge ? <span className="rounded-full bg-rose-100 px-1.5 text-[11px] font-semibold text-rose-800 dark:bg-rose-900 dark:text-rose-100">{t.badge}</span> : null}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {tab === 'obligations' && <Obligations rows={page.obligations} />}
        {tab === 'deadlines' && <Deadlines page={page} />}
        {tab === 'drivers' && <Drivers rows={page.drivers} onOpen={setDriver} />}
        {tab === 'numbers' && <Numbers page={page} onChanged={refresh} />}
      </div>

      {driver && <DriverRecordsDialog driver={driver} onClose={() => setDriver(null)} onChanged={refresh} />}
    </div>
  )
}

function Stat({ label, value, tone, sub }: { label: string; value: number; tone?: string; sub?: string }) {
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${tone ?? 'text-foreground'}`}>{value}</div>
      {sub && <div className="text-[11px] text-rose-700 dark:text-rose-300">{sub}</div>}
    </div>
  )
}

function Pill({ status }: { status: Obligation['status'] }) {
  const s = OBLIGATION_STATUS[status]
  return <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${s.tone}`}>{s.label}</span>
}

function Obligations({ rows }: { rows: Obligation[] }) {
  const [area, setArea] = useState('')
  const [status, setStatus] = useState('')
  const [q, setQ] = useState('')
  const areas = useMemo(() => Array.from(new Set(rows.map((r) => r.area))), [rows])
  const shown = rows.filter((r) =>
    (!area || r.area === area) && (!status || r.status === status) &&
    (!q || `${r.clause} ${r.obligation} ${r.gap} ${r.build_item}`.toLowerCase().includes(q.toLowerCase())))
  return (
    <div className="space-y-3 p-4">
      <div className="flex flex-wrap gap-2">
        <NativeSelect value={area} onChange={(e) => setArea(e.target.value)} className="w-56">
          <option value="">Every area</option>
          {areas.map((a) => <option key={a} value={a}>{a}</option>)}
        </NativeSelect>
        <NativeSelect value={status} onChange={(e) => setStatus(e.target.value)} className="w-40">
          <option value="">Every status</option>
          <option value="missing">Missing</option>
          <option value="partial">Partial</option>
          <option value="in_place">In place</option>
        </NativeSelect>
        <Input placeholder="Search a clause or a word" value={q} onChange={(e) => setQ(e.target.value)} className="w-64" />
        <span className="self-center text-xs text-muted-foreground">{shown.length} of {rows.length}</span>
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Clause</th><th className="px-3 py-2">Obligation</th><th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Gap</th><th className="px-3 py-2">Build item</th><th className="px-3 py-2">Wave</th><th className="px-3 py-2">Live</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className="border-t border-border align-top">
                <td className="whitespace-nowrap px-3 py-2 font-medium tabular-nums">{r.clause}</td>
                <td className="px-3 py-2">
                  <div>{r.obligation}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">Found: {r.found}</div>
                  {r.status_evidence && <div className="mt-0.5 text-xs text-emerald-700 dark:text-emerald-300">{r.status_evidence}</div>}
                </td>
                <td className="px-3 py-2"><Pill status={r.status} />{r.status_changed_at && <div className="mt-1 text-[11px] text-muted-foreground">since {day(r.status_changed_at)}</div>}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.gap}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.build_item}</td>
                <td className="px-3 py-2 tabular-nums">{r.wave}</td>
                <td className="whitespace-nowrap px-3 py-2 text-xs">
                  {r.open_deadlines > 0 && <div>{r.open_deadlines} open</div>}
                  {r.overdue_deadlines > 0 && <div className="text-rose-700 dark:text-rose-300">{r.overdue_deadlines} overdue</div>}
                  {r.records > 0 && <div className="text-muted-foreground">{r.records} records</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Deadlines({ page }: { page: CompliancePage }) {
  const [groupBy, setGroupBy] = useState<'clause' | 'owner'>('clause')
  const groups = useMemo(() => {
    const m = new Map<string, typeof page.deadlines>()
    for (const d of page.deadlines) {
      const keys = groupBy === 'clause' ? [`${d.clause}: ${d.title}`] : (d.owners.length ? d.owners : ['Nobody'])
      for (const k of keys) m.set(k, [...(m.get(k) ?? []), d])
    }
    return Array.from(m.entries()).sort((a, b) => b[1].filter((d) => d.overdue_days > 0).length - a[1].filter((d) => d.overdue_days > 0).length)
  }, [page.deadlines, groupBy])
  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Group by</span>
        <NativeSelect value={groupBy} onChange={(e) => setGroupBy(e.target.value as 'clause' | 'owner')} className="w-36">
          <option value="clause">Clause</option>
          <option value="owner">Owner</option>
        </NativeSelect>
        <span className="text-xs text-muted-foreground">Each deadline is a task on the task board. Finishing it there finishes it here.</span>
      </div>
      {page.deadlines.length === 0 && <p className="text-sm text-muted-foreground">No contract deadline is open.</p>}
      {groups.map(([key, rows]) => (
        <section key={key} className="rounded-md border border-border">
          <h3 className="border-b border-border bg-muted/40 px-3 py-2 text-sm font-semibold">{key} <span className="font-normal text-muted-foreground">({rows.length})</span></h3>
          <ul className="divide-y divide-border">
            {rows.map((d) => (
              <li key={d.id} className="flex flex-wrap items-start gap-3 px-3 py-2 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{d.driver ?? d.title}</div>
                  {d.detail && <div className="text-xs text-muted-foreground">{d.detail}</div>}
                  <div className="text-xs text-muted-foreground">{d.owners.join(', ')}{d.task_ref ? ` · task ${d.task_ref}` : ''}{d.escalation_level ? ` · escalated (level ${d.escalation_level})` : ''}</div>
                </div>
                <div className={`whitespace-nowrap text-xs tabular-nums ${d.overdue_days > 0 ? 'font-semibold text-rose-700 dark:text-rose-300' : 'text-muted-foreground'}`}>
                  due {day(d.due_on)}{d.overdue_days > 0 ? `, ${d.overdue_days} day${d.overdue_days === 1 ? '' : 's'} overdue` : ''}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
      {page.recently_closed.length > 0 && (
        <section className="rounded-md border border-border">
          <h3 className="border-b border-border bg-muted/40 px-3 py-2 text-sm font-semibold">Closed in the last 30 days</h3>
          <ul className="divide-y divide-border">
            {page.recently_closed.map((d, i) => (
              <li key={i} className="flex flex-wrap gap-3 px-3 py-2 text-sm">
                <span className="min-w-0 flex-1">{d.clause}: {d.title}{d.driver ? `, ${d.driver}` : ''}</span>
                <span className="text-xs text-muted-foreground">{d.status === 'done' ? 'done' : 'cancelled'} {day(d.done_at)} by {d.done_by}</span>
                {d.late && <span className="text-xs font-semibold text-rose-700 dark:text-rose-300">late (due {day(d.due_on)})</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

function Drivers({ rows, onOpen }: { rows: DriverRow[]; onOpen: (d: DriverRow) => void }) {
  return (
    <div className="p-4">
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
            <tr><th className="px-3 py-2">Driver</th><th className="px-3 py-2">Terms today</th><th className="px-3 py-2">Records</th><th className="px-3 py-2">To acknowledge</th><th className="px-3 py-2">Open deadlines</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.driver_id} className="border-t border-border">
                <td className="px-3 py-2 font-medium">{d.name}{d.status !== 'active' && <span className="ml-1 text-xs text-muted-foreground">({d.status})</span>}</td>
                <td className="px-3 py-2">
                  <div>{d.terms}</div>
                  {d.agreement_signed_on && <div className="text-xs text-muted-foreground">signed {day(d.agreement_signed_on)}{d.addendum_terms_from ? `, new terms from ${day(d.addendum_terms_from)}` : ''}</div>}
                </td>
                <td className="px-3 py-2 tabular-nums">{d.records}</td>
                <td className="px-3 py-2 tabular-nums">{d.awaiting}{d.overdue > 0 && <span className="ml-1 text-xs font-semibold text-rose-700 dark:text-rose-300">{d.overdue} past date</span>}</td>
                <td className="px-3 py-2 tabular-nums">{d.open_deadlines}</td>
                <td className="px-3 py-2 text-right"><Button size="sm" variant="outline" onClick={() => onOpen(d)}><FileText className="mr-1 h-3.5 w-3.5" />Records</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function DriverRecordsDialog({ driver, onClose, onChanged }: { driver: DriverRow; onClose: () => void; onChanged: () => void }) {
  const q = useGetDriverRecords<DriverRecords | null>()
  const load = q.trigger
  const reload = useCallback(() => { void load({ driver_id: driver.driver_id }, { skipCache: true }).catch(() => undefined) }, [load, driver.driver_id])
  useEffect(() => { reload() }, [reload])
  const data = q.data
  const changed = () => { reload(); onChanged() }
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{driver.name}: written records</DialogTitle>
          <DialogDescription>Terms today: {driver.terms}. Signing a record shows he received it. It never means he agrees.</DialogDescription>
        </DialogHeader>
        {q.loading && !data && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading…</div>}
        {q.error && <p className="text-sm text-rose-700 dark:text-rose-300">{q.error}</p>}
        {data && data.records.length === 0 && <p className="text-sm text-muted-foreground">No written record has been given to {driver.name} on the platform yet.</p>}
        {data && data.records.map((r) => <RecordCard key={r.id} r={r} data={data} onChanged={changed} />)}
      </DialogContent>
    </Dialog>
  )
}

function RecordCard({ r, data, onChanged }: { r: EmployeeRecord; data: DriverRecords; onChanged: () => void }) {
  const refusal = useRecordRecordRefusal()
  const witness = useConfirmRecordWitness()
  const signUpload = useSignRecordUpload()
  const paper = useRecordPaperRecord()
  const voidIt = useVoidEmployeeRecord()
  const openFile = useOpenRecordFile()
  const [f, setF] = useState<Record<string, string>>({})
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(r.status === 'awaiting_signature' || r.status === 'refused_awaiting_witness')
  const set = (k: string) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }))
  async function run(done: string, fn: () => Promise<unknown>) {
    setBusy(true)
    try { await fn(); toast.success(done); setF({}); setFile(null); onChanged() } catch (e) { toast.error(writeErrorMessage(e)) } finally { setBusy(false) }
  }
  const actionable = r.status === 'awaiting_signature' || r.status === 'refused_awaiting_witness'
  return (
    <section className="space-y-2 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">{r.title ?? r.label} <span className="font-normal text-muted-foreground">clause {r.clause}</span></div>
          <div className="text-xs text-muted-foreground">
            Given {day(r.given_on)}{r.given_by ? ` by ${r.given_by}` : ''}. {RECORD_STATUS[r.status] ?? r.status}
            {r.signed_at ? `, ${day(r.signed_at)}` : ''}{r.paper_signed_on ? `, signed ${day(r.paper_signed_on)}` : ''}
            {r.witness ? `. Witness: ${r.witness}${r.witness_confirmed_at ? ', confirmed' : ', not yet confirmed'}` : ''}
            {r.message_status ? `. ${MESSAGE_STATUS[r.message_status] ?? r.message_status}` : ''}
            {r.overdue ? '. Past the date to acknowledge.' : ''}
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={() => setOpen((o) => !o)}>{open ? 'Hide' : 'Show'}</Button>
      </div>
      {open && (
        <>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded bg-muted/50 p-3 text-xs">{r.document_text}</pre>
          {r.response && <p className="text-sm"><span className="font-medium">His response:</span> {r.response}</p>}
          {r.void_reason && <p className="text-sm text-muted-foreground">Voided: {r.void_reason}</p>}
          {r.paper_path && (
            <Button size="sm" variant="outline" disabled={busy}
              onClick={() => void run('Opened.', async () => {
                const res = (await openFile.trigger({ path: r.paper_path }, { skipCache: true })) as { url?: string } | null
                if (res?.url) window.open(res.url, '_blank', 'noopener')
              })}>Open the signed paper</Button>
          )}
          {r.status === 'refused_awaiting_witness' && r.witness_person_id === data.me && (
            <div className="space-y-2 rounded border border-amber-300 p-2">
              <p className="text-sm">You were named as the witness. Confirm only if you saw it given to him.</p>
              <Textarea placeholder="Anything to add (optional)" value={f.wnote ?? ''} onChange={set('wnote')} />
              <Button size="sm" disabled={busy} onClick={() => void run('Confirmed.', () => witness.trigger({ record_id: r.id, note: f.wnote ?? '' }))}>I saw it given to him</Button>
            </div>
          )}
          {r.status === 'awaiting_signature' && (
            <div className="space-y-2">
              <p className="text-sm font-medium">He refused, or did not acknowledge it in time</p>
              <div className="grid gap-2 sm:grid-cols-3">
                <NativeSelect value={f.kind ?? ''} onChange={set('kind')}>
                  <option value="">Which</option>
                  <option value="refused_in_person">Refused in person</option>
                  <option value="not_signed_in_time">Did not acknowledge in time</option>
                </NativeSelect>
                <Input type="date" value={f.on ?? ''} max={todaySast()} onChange={set('on')} />
                <NativeSelect value={f.wit ?? ''} onChange={set('wit')}>
                  <option value="">Witness</option>
                  {data.staff.filter((x) => x.person_id !== data.me).map((x) => <option key={x.person_id} value={x.person_id}>{x.name}</option>)}
                </NativeSelect>
              </div>
              <Textarea placeholder="When and where it was given, and what he said" value={f.rnote ?? ''} onChange={set('rnote')} />
              <Button size="sm" variant="outline" disabled={busy}
                onClick={() => void run('Recorded. The witness has a task to confirm it.', () =>
                  refusal.trigger({ record_id: r.id, kind: f.kind, refused_on: f.on, witness_person_id: f.wit, note: f.rnote }))}>
                Record with the witness
              </Button>
            </div>
          )}
          {actionable && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Paper: a photo of the copy he signed</p>
              <div className="grid gap-2 sm:grid-cols-2">
                <Input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                <Input type="date" value={f.pon ?? ''} max={todaySast()} onChange={set('pon')} />
              </div>
              <Textarea placeholder="His written response on the paper, if any" value={f.presp ?? ''} onChange={set('presp')} />
              <Button size="sm" variant="outline" disabled={busy || !file}
                onClick={() => void run('Signed paper copy filed.', async () => {
                  if (!file) throw new Error('Choose the photo first.')
                  if (file.size > 10 * 1024 * 1024) throw new Error('That photo is over 10 MB. Take it again at a lower size.')
                  const signed = (await signUpload.trigger({ record_id: r.id, filename: file.name }, { skipCache: true })) as { signedUrl?: string; path?: string } | null
                  if (!signed?.signedUrl || !signed.path) throw new Error('No upload address came back.')
                  const put = await fetch(signed.signedUrl, { method: 'PUT', headers: file.type ? { 'Content-Type': file.type } : undefined, body: file })
                  if (!put.ok) throw new Error(`Storage refused the photo (${put.status}).`)
                  await paper.trigger({ record_id: r.id, path: signed.path, signed_on: f.pon, response: f.presp ?? '' })
                })}>
                Upload and file it
              </Button>
            </div>
          )}
          {r.status !== 'void' && (
            <div className="flex flex-wrap items-center gap-2 border-t border-border pt-2">
              <Input placeholder="Why it was given in error" value={f.vreason ?? ''} onChange={set('vreason')} className="max-w-sm" />
              <Button size="sm" variant="ghost" disabled={busy || !(f.vreason ?? '').trim()}
                onClick={() => void run('Voided. It stays on file.', () => voidIt.trigger({ record_id: r.id, reason: f.vreason }))}>Void it</Button>
            </div>
          )}
        </>
      )}
    </section>
  )
}

function Numbers({ page, onChanged }: { page: CompliancePage; onChanged: () => void }) {
  const [editing, setEditing] = useState<Setting | null>(null)
  return (
    <div className="space-y-4 p-4">
      {page.drift.length > 0 && (
        <section className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950">
          <h3 className="mb-1 flex items-center gap-1 font-semibold"><Gauge className="h-4 w-4" /> An older settings table disagrees with the agreement</h3>
          <ul className="list-disc pl-5">
            {page.drift.map((d, i) => (
              <li key={i}>Clause {d.clause}: the agreement says {d.contract_value}; {d.source} {d.source_key} says {d.source_value ?? 'nothing'} ({d.finding}).</li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-muted-foreground">The rule that reads the older table uses its number until it is brought into line.</p>
        </section>
      )}
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
            <tr><th className="px-3 py-2">Clause</th><th className="px-3 py-2">Number</th><th className="px-3 py-2">Value</th><th className="px-3 py-2">Since</th><th className="px-3 py-2">Note</th>{page.is_admin && <th />}</tr>
          </thead>
          <tbody>
            {page.settings.map((x) => (
              <tr key={x.key} className="border-t border-border">
                <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">{x.clause}</td>
                <td className="px-3 py-1.5 font-mono text-xs">{x.key}</td>
                <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">{x.value} <span className="text-xs text-muted-foreground">{x.unit}</span></td>
                <td className="whitespace-nowrap px-3 py-1.5 text-xs text-muted-foreground">{day(x.from)}</td>
                <td className="px-3 py-1.5 text-xs text-muted-foreground">{x.note}</td>
                {page.is_admin && <td className="px-3 py-1.5 text-right"><Button size="sm" variant="ghost" onClick={() => setEditing(x)}>Change</Button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <ChangeSetting s={editing} onClose={() => setEditing(null)} onChanged={() => { setEditing(null); onChanged() }} />}
    </div>
  )
}

function ChangeSetting({ s, onClose, onChanged }: { s: Setting; onClose: () => void; onChanged: () => void }) {
  const change = useChangeContractSetting()
  const [value, setValue] = useState(s.value)
  const [from, setFrom] = useState(todaySast())
  const [note, setNote] = useState('')
  const numeric = /^-?\d+(\.\d+)?$/.test(s.value)
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change {s.key}</DialogTitle>
          <DialogDescription>Clause {s.clause}. The current value is closed on the day the new one starts. Past decisions keep the number they used.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input value={value} onChange={(e) => setValue(e.target.value)} />
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <Textarea placeholder="Why it changes, and where that is written: the clause, the notice given, or the signed variation" value={note} onChange={(e) => setNote(e.target.value)} />
          <Button disabled={change.loading} onClick={() => void change.trigger({ key: s.key, from, value_num: numeric ? value : null, value_text: numeric ? null : value, note })
            .then(() => { toast.success('Changed.'); onChanged() }).catch((e) => toast.error(writeErrorMessage(e)))}>Save the change</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
